import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { listAwaitingReviewAttempts } from "@/api/listAwaitingReviewAttempts";
import { fetchAttemptForReview } from "@/api/fetchAttemptForReview";
import { saveCoachResponses } from "@/api/saveCoachResponses";
import { completeAttempt } from "@/api/completeAttempt";
import {
  CoachAttemptDetail,
  ReviewQueueRow,
  SaveCoachResponsesPayload,
} from "../types/diagnostic";

interface DiagnosticReviewState {
  queue: ReviewQueueRow[];
  queueLoading: boolean;
  queueError: string | null;

  currentAttempt: CoachAttemptDetail | null;
  attemptLoading: boolean;
  attemptError: string | null;

  saveStatus: "idle" | "saving" | "saved" | "error";
  saveError: string | null;

  completeStatus: "idle" | "completing" | "completed" | "error";
  completeError: string | null;
}

const initialState: DiagnosticReviewState = {
  queue: [],
  queueLoading: false,
  queueError: null,

  currentAttempt: null,
  attemptLoading: false,
  attemptError: null,

  saveStatus: "idle",
  saveError: null,

  completeStatus: "idle",
  completeError: null,
};

const errorMessage = (e: unknown, fallback: string): string => {
  if (typeof e === "object" && e !== null && "response" in e) {
    const r = (
      e as {
        response?: {
          data?: {
            detail?: string;
            message?: string;
            errors?: { detail?: string };
          };
        };
      }
    ).response;
    return (
      r?.data?.errors?.detail ?? r?.data?.detail ?? r?.data?.message ?? fallback
    );
  }
  return fallback;
};

export const fetchReviewQueueThunk = createAsyncThunk<
  ReviewQueueRow[],
  void,
  { rejectValue: string }
>("diagnosticReview/queue", async (_, { rejectWithValue }) => {
  try {
    return await listAwaitingReviewAttempts();
  } catch (e: unknown) {
    return rejectWithValue(errorMessage(e, "Failed to load queue."));
  }
});

export const fetchAttemptForReviewThunk = createAsyncThunk<
  CoachAttemptDetail,
  number,
  { rejectValue: string }
>("diagnosticReview/fetch", async (attemptId, { rejectWithValue }) => {
  try {
    return await fetchAttemptForReview(attemptId);
  } catch (e: unknown) {
    return rejectWithValue(errorMessage(e, "Failed to load attempt."));
  }
});

export const saveCoachResponsesThunk = createAsyncThunk<
  CoachAttemptDetail,
  { attemptId: number; payload: SaveCoachResponsesPayload },
  { rejectValue: string }
>(
  "diagnosticReview/save",
  async ({ attemptId, payload }, { rejectWithValue }) => {
    try {
      return await saveCoachResponses(attemptId, payload);
    } catch (e: unknown) {
      return rejectWithValue(errorMessage(e, "Failed to save."));
    }
  },
);

export const completeAttemptThunk = createAsyncThunk<
  CoachAttemptDetail,
  number,
  { rejectValue: string }
>("diagnosticReview/complete", async (attemptId, { rejectWithValue }) => {
  try {
    return await completeAttempt(attemptId);
  } catch (e: unknown) {
    return rejectWithValue(errorMessage(e, "Failed to complete review."));
  }
});

const slice = createSlice({
  name: "diagnosticReview",
  initialState,
  reducers: {
    clearReviewAttempt: (state) => {
      state.currentAttempt = null;
      state.attemptError = null;
      state.saveStatus = "idle";
      state.saveError = null;
      state.completeStatus = "idle";
      state.completeError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReviewQueueThunk.pending, (s) => {
        s.queueLoading = true;
        s.queueError = null;
      })
      .addCase(fetchReviewQueueThunk.fulfilled, (s, a) => {
        s.queueLoading = false;
        s.queue = a.payload;
      })
      .addCase(fetchReviewQueueThunk.rejected, (s, a) => {
        s.queueLoading = false;
        s.queueError = a.payload ?? "Failed.";
      })

      .addCase(fetchAttemptForReviewThunk.pending, (s) => {
        s.attemptLoading = true;
        s.attemptError = null;
      })
      .addCase(fetchAttemptForReviewThunk.fulfilled, (s, a) => {
        s.attemptLoading = false;
        s.currentAttempt = a.payload;
      })
      .addCase(fetchAttemptForReviewThunk.rejected, (s, a) => {
        s.attemptLoading = false;
        s.attemptError = a.payload ?? "Failed.";
      })

      .addCase(saveCoachResponsesThunk.pending, (s) => {
        s.saveStatus = "saving";
        s.saveError = null;
      })
      .addCase(saveCoachResponsesThunk.fulfilled, (s, a) => {
        s.saveStatus = "saved";
        s.currentAttempt = a.payload;
      })
      .addCase(saveCoachResponsesThunk.rejected, (s, a) => {
        s.saveStatus = "error";
        s.saveError = a.payload ?? "Failed.";
      })

      .addCase(completeAttemptThunk.pending, (s) => {
        s.completeStatus = "completing";
        s.completeError = null;
      })
      .addCase(completeAttemptThunk.fulfilled, (s, a) => {
        s.completeStatus = "completed";
        s.currentAttempt = a.payload;
      })
      .addCase(completeAttemptThunk.rejected, (s, a) => {
        s.completeStatus = "error";
        s.completeError = a.payload ?? "Failed.";
      });
  },
});

export const { clearReviewAttempt } = slice.actions;
export default slice.reducer;
